
import { IonContent, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

// Question interface
interface QuestionData {
    question: string;
    correct: boolean;
}


const MyHomeTrueFalse: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'MyHomeTrueFalse.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/MyHomeTrueFalse.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        // Simulating an API fetch to load questions
        fetchQuestions();
        document.title = "My Home – True or False Worksheet for UKG | Mama’s Guidance"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleOptionClick = (selectedMyOption: boolean) => {
        console.log(selectedMyOption)
        const currentQuestion = questions[currentIndex];
        setSelectedOption(selectedMyOption);
        if (selectedMyOption === currentQuestion.correct) {
            setError(false);
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                }
                setShowImage(false);
                setError(false);
                setSelectedOption(null)
            }, 5000); // Shortened delay to 3 seconds
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Simple true or false worksheet for UKG on the topic My Home. Let kids learn parts of a house and household objects with fun and clarity." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/my-home/true-false" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart seasons additionWord" style={{ marginTop: "0%" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>My Home – True or False</h1>

                                    <p>
                                        Help young learners understand their surroundings with this engaging <strong>True or False</strong> worksheet based on the topic <strong>My Home</strong>. It’s perfect for UKG children to identify rooms, household items, and their correct uses.
                                    </p>

                                    <h2>🏠 What’s Covered in This Worksheet?</h2>
                                    <ul>
                                        <li>Identifying rooms in a house (bedroom, kitchen, bathroom, etc.)</li>
                                        <li>Recognizing objects and their correct placements</li>
                                        <li>Basic home-related facts presented as simple statements</li>
                                    </ul>

                                    <h2>📘 Worksheet Format</h2>
                                    <p>
                                        The worksheet consists of <strong>simple true or false questions</strong> that encourage observational learning and basic comprehension. Statements are age-appropriate and revolve around things children see every day at home.
                                    </p>

                                    <h2>🔍 Sample Questions</h2>
                                    <ul>
                                        <li>We sleep in the kitchen. – <strong>False</strong></li>
                                        <li>A bathroom has a shower. – <strong>True</strong></li>
                                        <li>We watch TV in the bedroom. – <strong>Sometimes True</strong></li>
                                        <li>A dining table is found in the dining room. – <strong>True</strong></li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Sense Organs</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-home"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My Home</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-school"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My School</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why Use True or False Worksheets?</h2>
                                    <ul>
                                        <li>Encourages critical thinking and analysis</li>
                                        <li>Boosts vocabulary through repetition of common words</li>
                                        <li>Improves sentence comprehension and logic</li>
                                        <li>Great for independent or group activity time</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>My Home – True or False</h1>

                                <p>
                                    Help young learners understand their surroundings with this engaging <strong>True or False</strong> worksheet based on the topic <strong>My Home</strong>. It’s perfect for UKG children to identify rooms, household items, and their correct uses.
                                </p>

                                <h2>🏠 What’s Covered in This Worksheet?</h2>
                                <ul>
                                    <li>Identifying rooms in a house (bedroom, kitchen, bathroom, etc.)</li>
                                    <li>Recognizing objects and their correct placements</li>
                                    <li>Basic home-related facts presented as simple statements</li>
                                </ul>

                                <h2>📘 Worksheet Format</h2>
                                <p>
                                    The worksheet consists of <strong>simple true or false questions</strong> that encourage observational learning and basic comprehension. Statements are age-appropriate and revolve around things children see every day at home.
                                </p>

                                <h2>🔍 Sample Questions</h2>
                                <ul>
                                    <li>We sleep in the kitchen. – <strong>False</strong></li>
                                    <li>A bathroom has a shower. – <strong>True</strong></li>
                                    <li>We watch TV in the bedroom. – <strong>Sometimes True</strong></li>
                                    <li>A dining table is found in the dining room. – <strong>True</strong></li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why Use True or False Worksheets?</h2>
                                <ul>
                                    <li>Encourages critical thinking and analysis</li>
                                    <li>Boosts vocabulary through repetition of common words</li>
                                    <li>Improves sentence comprehension and logic</li>
                                    <li>Great for independent or group activity time</li>
                                </ul>
                            </article>
                            <div className="mt-2 mb-4">
                                <h3 className="mb-2">
                                    {questions[currentIndex]?.question}
                                </h3>
                            </div>

                            <IonRadioGroup onIonChange={(e) => handleOptionClick(e.detail.value!)}>
                                <IonItem
                                    className={`grid-item ${selectedOption === true
                                        ? true === questions[currentIndex]?.correct
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}>
                                    <IonRadio slot="start" value={true} />
                                    <IonLabel>True</IonLabel>
                                </IonItem>
                                <IonItem
                                    className={`grid-item ${selectedOption === false
                                        ? false === questions[currentIndex]?.correct
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}>
                                    <IonRadio slot="start" value={false} />
                                    <IonLabel>False</IonLabel>
                                </IonItem>
                            </IonRadioGroup>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default MyHomeTrueFalse;
